// src/pages/Admin/Components/DiscountsPage.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Percent, 
  Search, 
  X, 
  Loader2, 
  ChevronRight, 
  ChevronLeft,
  Save,
  RefreshCw,
  Filter,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { adminUpdateProduct } from '../../../services/adminServices';
import { fetchProducts } from '../../../redux/features/Product/productThunk';

const COLORS = {
  primary: '#8A9A7B',
  primaryLight: '#9EAD8C',
  primaryDark: '#6A7A5B',
  primaryBg: '#F0F4EB',
  border: '#DCE4D3',
  danger: '#D4837A',
  success: '#7A9B8A',
  warning: '#E8A87C',
};

const ProductRow = ({ product, onDiscountChange, onSave, saving, isAdmin }) => {
  const [discount, setDiscount] = useState(product.discountPercentage || 0);
  const [isDirty, setIsDirty] = useState(false);
  const [localSaving, setLocalSaving] = useState(false);

  const hasDiscount = product.discountPercentage > 0;
  const finalPrice = product.price * (1 - product.discountPercentage / 100);

  const handleDiscountChange = (e) => {
    const value = parseInt(e.target.value) || 0;
    const clamped = Math.min(100, Math.max(0, value));
    setDiscount(clamped);
    setIsDirty(clamped !== (product.discountPercentage || 0));
  };

  const handleSave = async () => {
    if (!isDirty) return;
    setLocalSaving(true);
    try {
      await onSave(product._id, { discountPercentage: discount });
      setIsDirty(false);
    } catch (err) {
      console.error('Error saving discount:', err);
    } finally {
      setLocalSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    }
  };

  const isSaving = saving === product._id || localSaving;

  return (
    <tr className="hover:bg-gray-50/50 transition-colors border-b border-gray-100">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <img 
            src={product.image || 'https://via.placeholder.com/48'} 
            alt={product.name}
            className="w-12 h-12 rounded-lg object-cover bg-gray-50"
            onError={(e) => e.target.src = 'https://via.placeholder.com/48?text=No+Image'}
          />
          <div>
            <p className="text-sm font-bold text-gray-800 line-clamp-1">{product.name}</p>
            <p className="text-xs text-gray-400">{product.category?.name || 'دسته‌بندی نشده'}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-center">
        <span className="text-sm font-medium text-gray-700">
          {product.price.toLocaleString()} تومان
        </span>
      </td>
      <td className="px-4 py-3 text-center">
        <span className={`text-sm font-bold ${hasDiscount ? 'text-[#7A9B8A]' : 'text-gray-400'}`}>
          {hasDiscount ? Math.round(finalPrice).toLocaleString() : '---'} تومان
        </span>
      </td>
      <td className="px-4 py-3 text-center">
        {hasDiscount ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-[#F0F4EB] text-[#7A9B8A] border border-[#7A9B8A]/30">
            <CheckCircle className="w-3.5 h-3.5" />
            فعال
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-400 border border-gray-200">
            <XCircle className="w-3.5 h-3.5" />
            غیرفعال
          </span>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          <input
            type="number"
            min="0"
            max="100"
            value={discount}
            onChange={handleDiscountChange}
            onKeyDown={handleKeyDown}
            className="w-20 text-center border-2 border-gray-200 rounded-lg px-2 py-1.5 text-sm font-bold outline-none transition-all focus:border-[#8A9A7B] focus:ring-2 focus:ring-[#8A9A7B]/20"
            disabled={isSaving}
          />
          <span className="text-sm text-gray-400">%</span>
        </div>
      </td>
      <td className="px-4 py-3 text-center">
        <span className={`text-xs font-medium ${product.countInStock > 0 ? 'text-gray-700' : 'text-red-400'}`}>
          {product.countInStock || 0}
        </span>
      </td>
      <td className="px-4 py-3 text-center">
        <button
          onClick={handleSave}
          disabled={!isDirty || isSaving}
          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
            isDirty && !isSaving
              ? 'bg-[#8A9A7B] text-white hover:bg-[#6A7A5B] shadow-md'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}
        >
          {isSaving ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Save className="w-3.5 h-3.5" />
          )}
          {isSaving ? 'در حال...' : 'ذخیره'}
        </button>
      </td>
    </tr>
  );
};

const SkeletonRow = () => (
  <tr className="animate-pulse border-b border-gray-100">
    <td className="px-4 py-3"><div className="flex items-center gap-3"><div className="w-12 h-12 bg-gray-200 rounded-lg"></div><div><div className="h-4 w-32 bg-gray-200 rounded"></div><div className="h-3 w-20 bg-gray-200 rounded mt-1"></div></div></div></td>
    <td className="px-4 py-3 text-center"><div className="h-4 w-20 bg-gray-200 rounded mx-auto"></div></td>
    <td className="px-4 py-3 text-center"><div className="h-4 w-20 bg-gray-200 rounded mx-auto"></div></td>
    <td className="px-4 py-3 text-center"><div className="h-6 w-16 bg-gray-200 rounded-full mx-auto"></div></td>
    <td className="px-4 py-3"><div className="h-9 w-24 bg-gray-200 rounded-lg mx-auto"></div></td>
    <td className="px-4 py-3 text-center"><div className="h-4 w-12 bg-gray-200 rounded mx-auto"></div></td>
    <td className="px-4 py-3 text-center"><div className="h-8 w-16 bg-gray-200 rounded-lg mx-auto"></div></td>
  </tr>
);

const DiscountsPage = () => {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.auth) || {};
  const { products, loading: productsLoading } = useSelector((state) => state.listProduct) || { products: [], loading: false };

  const [saving, setSaving] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const itemsPerPage = 10;

  useEffect(() => {
    if (!products || products.length === 0) {
      dispatch(fetchProducts());
    }
  }, [dispatch, products]);

  const filteredProducts = useMemo(() => {
    let result = [...products];
    if (searchTerm.trim()) {
      result = result.filter(p => 
        p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.category?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (filterStatus === 'active') {
      result = result.filter(p => p.discountPercentage > 0);
    } else if (filterStatus === 'inactive') {
      result = result.filter(p => !p.discountPercentage || p.discountPercentage === 0);
    }
    return result;
  }, [products, searchTerm, filterStatus]);

  const totalItems = filteredProducts.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const currentProducts = filteredProducts.slice(startIndex, endIndex);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleSaveDiscount = async (productId, data) => {
    setSaving(productId);
    setError('');
    setSuccessMessage('');
    try {
      await adminUpdateProduct(productId, data, { userInfo, dispatch });
      setSuccessMessage('✅ تخفیف با موفقیت به‌روزرسانی شد');
      setTimeout(() => setSuccessMessage(''), 3000);
      dispatch(fetchProducts());
    } catch (err) {
      setError(err.response?.data?.detail || 'خطا در به‌روزرسانی تخفیف');
      setTimeout(() => setError(''), 4000);
    } finally {
      setSaving(null);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    dispatch(fetchProducts());
  };

  const getPaginationButtons = () => {
    const buttons = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) buttons.push(i);
    } else {
      buttons.push(1);
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);
      if (currentPage <= 2) end = 4;
      if (currentPage >= totalPages - 1) start = totalPages - 3;
      if (start > 2) buttons.push('...');
      for (let i = start; i <= end; i++) buttons.push(i);
      if (end < totalPages - 1) buttons.push('...');
      buttons.push(totalPages);
    }
    return buttons;
  };

  return (
    <div className="flex-1 min-w-0 p-4 md:p-6 lg:p-8 bg-gray-50 min-h-screen" dir="rtl">
      
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 border-b border-gray-200 pb-4">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-800 flex items-center gap-3">
            <Percent className="w-7 h-7 text-[#8A9A7B]" />
            مدیریت تخفیفات
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            مدیریت درصد تخفیف محصولات فروشگاه
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={productsLoading}
          className="mt-3 md:mt-0 flex items-center gap-2 px-4 py-2.5 bg-[#8A9A7B] text-white rounded-xl font-bold hover:bg-[#6A7A5B] transition-colors shadow-md disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${productsLoading ? 'animate-spin' : ''}`} />
          {productsLoading ? 'در حال بارگذاری...' : 'بروزرسانی'}
        </button>
      </div>

      {successMessage && (
        <div className="mb-4 p-3 bg-[#F0F4EB] border border-[#7A9B8A] rounded-xl text-[#7A9B8A] text-sm">
          {successMessage}
        </div>
      )}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
          <AlertCircle className="w-4 h-4 inline ml-1.5" />
          {error}
        </div>
      )}

      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 mb-6 flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="flex flex-wrap gap-2 w-full md:w-auto">
          {[
            { id: 'all', label: 'همه محصولات' },
            { id: 'active', label: 'تخفیف‌دار' },
            { id: 'inactive', label: 'بدون تخفیف' },
          ].map((filter) => (
            <button
              key={filter.id}
              onClick={() => { setFilterStatus(filter.id); setCurrentPage(1); }}
              className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                filterStatus === filter.id
                  ? 'bg-[#8A9A7B] text-white shadow-md shadow-[#8A9A7B]/20'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {filter.label}
              {filter.id === 'active' && (
                <span className="mr-1.5 text-xs bg-white/20 px-1.5 py-0.5 rounded-full">
                  {products.filter(p => p.discountPercentage > 0).length}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="relative w-full md:w-64">
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <Search className="w-4 h-4 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="جستجو بر اساس نام یا دسته..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            className="w-full border border-gray-200 rounded-lg px-4 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#8A9A7B] transition-all"
          />
          {searchTerm && (
            <button onClick={clearSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="w-full overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-right text-xs font-bold text-gray-600 uppercase tracking-wider">
                <th className="px-4 py-3.5 text-right min-w-[200px]">محصول</th>
                <th className="px-4 py-3.5 text-center min-w-[100px]">قیمت اصلی</th>
                <th className="px-4 py-3.5 text-center min-w-[100px]">قیمت با تخفیف</th>
                <th className="px-4 py-3.5 text-center min-w-[80px]">وضعیت</th>
                <th className="px-4 py-3.5 text-center min-w-[100px]">درصد تخفیف</th>
                <th className="px-4 py-3.5 text-center min-w-[60px]">موجودی</th>
                <th className="px-4 py-3.5 text-center min-w-[80px] bg-gray-50 border-r border-gray-200">عملیات</th>
              </tr>
            </thead>
            <tbody>
              {productsLoading ? (
                Array(5).fill(0).map((_, i) => <SkeletonRow key={i} />)
              ) : currentProducts.length > 0 ? (
                currentProducts.map((product) => (
                  <ProductRow
                    key={product._id}
                    product={product}
                    onSave={handleSaveDiscount}
                    saving={saving}
                  />
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center py-12 text-gray-500">
                    <Percent className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="font-medium">محصولی یافت نشد</p>
                    <p className="text-sm text-gray-400 mt-1">با تغییر فیلترها یا جستجو، محصولات را پیدا کنید.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {!productsLoading && totalItems > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50 gap-4">
            <div className="text-sm text-gray-600 order-2 sm:order-1">
              نمایش <span className="font-bold text-gray-900">{startIndex + 1}</span> تا{' '}
              <span className="font-bold text-gray-900">{endIndex}</span> از{' '}
              <span className="font-bold text-gray-900">{totalItems}</span> محصول
            </div>
            <div className="flex items-center gap-2 order-1 sm:order-2 bg-white p-1 rounded-lg shadow-sm border border-gray-200">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="w-9 h-9 flex items-center justify-center rounded text-gray-500 hover:bg-gray-100 disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              {getPaginationButtons().map((btn, index) => (
                <button
                  key={index}
                  onClick={() => typeof btn === 'number' && goToPage(btn)}
                  className={`w-9 h-9 flex items-center justify-center rounded text-sm font-bold ${
                    btn === currentPage
                      ? 'bg-[#8A9A7B] text-white shadow-md shadow-[#8A9A7B]/20'
                      : btn === '...' ? 'text-gray-400 cursor-default' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  disabled={btn === '...'}
                >
                  {btn}
                </button>
              ))}
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="w-9 h-9 flex items-center justify-center rounded text-gray-500 hover:bg-gray-100 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 p-4 bg-blue-50/50 rounded-xl border border-blue-100 text-xs text-gray-500">
        <p className="font-bold text-gray-700 mb-1">💡 نکات:</p>
        <ul className="space-y-1 list-disc list-inside pr-4">
          <li>عدد بین <span className="font-bold">۰</span> تا <span className="font-bold">۱۰۰</span> را وارد کنید.</li>
          <li>با فشردن دکمه <span className="font-bold">Enter</span> نیز تغییرات ذخیره می‌شود.</li>
          <li>محصولات تخفیف‌دار در فروشگاه با قیمت جدید نمایش داده می‌شوند.</li>
        </ul>
      </div>
    </div>
  );
};

export default DiscountsPage;